<template>
  <BaseModule
    v-bind="$props"
    class="container content-slider"
    data-module="content-slider"
  >
    <div class="content">
      <header
        v-if="mainTitle || mainSubtitle || mainText"
        class="header base-title-fontsize"
      >
        <component
          :is="titleTag"
          v-if="mainTitle"
          class="tit tit-g"
        >
          {{ mainTitle }}
        </component>
        <p
          v-if="mainSubtitle"
          class="sub"
        >
          {{ mainSubtitle }}
        </p>
        <div
          v-if="mainText"
          class="txt"
          v-html="mainText"
        ></div>
      </header>

      <Warning
        v-if="slides.length === 0 && editing"
        text="O módulo não possui nenhum slide adicionado"
      >
      </Warning>

      <!-- Slider main container -->
      <div
        v-show="slides.length > 0"
        ref="slides-container"
        class="swiper"
      >
        <!-- Additional required wrapper -->
        <div class="swiper-wrapper">
          <!-- Slides -->
          <div
            v-for="(slide, idx) in slides"
            :key="slide.id || idx"
            class="swiper-slide"
          >
            <TextAndMedia
              :instance-id="`${instanceId}-${idx}`"
              :text-position="slide.textPosition"
              :title-weight="slide.titleWeight"
              :title="slide.title"
              :subtitle="slide.subtitle"
              :text="slide.text"
              :has-sticky-mode="false"
              :slides="[
                {
                  image: slide.image,
                  altText: slide.altText,
                  imageMobile: slide.imageMobile,
                  mobileAltText: slide.mobileAltText,
                  differentMobileBackgroundImage:
                    slide.differentMobileBackgroundImage,
                },
              ]"
              :buttons="
                slide.showButton
                  ? [
                      {
                        buttonFeatured: slide.buttonFeatured,
                        buttonText: slide.buttonText,
                        buttonUrl: slide.buttonUrl,
                      },
                    ]
                  : []
              "
            >
              <template #title>
                <h3 class="tit tit-g">
                  {{ slide.title }}
                </h3>
              </template>

              <template #subtitle>
                <h4 class="sub">
                  {{ slide.subtitle }}
                </h4>
              </template>

              <template #inner-text>
                <div
                  class="inner-text"
                  :class="{
                    'inner-text--show-more': shouldShowMore(idx),
                  }"
                >
                  <div
                    ref="inner"
                    v-html="slide.text"
                  ></div>
                </div>
              </template>

              <template #read-more>
                <button
                  v-if="isTextBig(idx) || shouldShowMore(idx)"
                  class="expand-text"
                  :class="{
                    'expand-text--active': shouldShowMore(idx),
                  }"
                  type="button"
                  @click="toggleReadMore(idx)"
                >
                  {{ $t('read') }}
                  {{ shouldShowMore(idx) ? $t('less') : $t('more') }}
                </button>
              </template>
            </TextAndMedia>
          </div>
        </div>

        <!-- If we need navigation buttons -->
        <div :class="`swiper-button-prev btn-prev-${instanceId}`"></div>
        <div :class="`swiper-button-next btn-next-${instanceId}`"></div>

        <!-- Add Pagination -->
        <div class="swiper-pagination"></div>
      </div>
    </div>
  </BaseModule>
</template>

<script>
import { mapState } from 'vuex';
import Swiper from 'swiper';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';

import base from '@/mixins/base';
import extendMetadata from '@/helpers/extendMetadata';
import Warning from '@/components/Warning.vue';
import BaseModule from './generic/BaseModule.vue';
import TextAndMedia from './generic/TextAndMedia.vue';

export default {
  name: 'ContentSlider',
  components: {
    BaseModule,
    TextAndMedia,
    Warning,
  },
  mixins: [base],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    mainTitle: {
      type: String,
      default: '',
    },
    mainSubtitle: {
      type: String,
      default: '',
    },
    mainText: {
      type: String,
      default: '',
    },
    slides: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      swiper: null,
      showMore: [],
      hasTextBig: [],
    };
  },
  computed: {
    editing() {
      return typeof this.$parent === 'undefined';
    },
    ...mapState(['currentEditableItem', 'activeInstance']),
  },
  watch: {
    currentEditableItem() {
      this.slideToActivatedItem();
    },
  },
  updated() {
    this.$nextTick(() => {
      this.swiper.updateAutoHeight();
    });
  },
  mounted() {
    this.updateAreaRelated();
    window.addEventListener('resize', this.updateAreaRelated);

    this.$nextTick(() => {
      this.swiper = new Swiper(this.$refs['slides-container'], {
        modules: [Autoplay, Navigation, Pagination],
        observer: true,
        spaceBetween: 7,
        slidesPerView: 'auto',
        slideToClickedSlide: true,
        centeredSlides: true,
        autoHeight: true,
        allowTouchMove: false,
        navigation: {
          nextEl: `.btn-next-${this.instanceId}`,
          prevEl: `.btn-prev-${this.instanceId}`,
        },
        breakpoints: {
          1020: {
            allowTouchMove: false,
          },
        },
        pagination: {
          el: '.swiper-pagination',
          type: 'bullets',
          clickable: true,
        },
        on: {
          observerUpdate: () => {
            this.swiper.slideToLoop(this.slides.length - 1);
          },
        },
      });
    });
  },
  methods: {
    toggleReadMore(index) {
      if (this.showMore.includes(index)) {
        this.showMore = this.showMore.filter((item) => item !== index);
      } else {
        this.showMore.push(index);
      }

      this.$nextTick(() => {
        this.swiper.updateAutoHeight();
      });
    },
    shouldShowMore(index) {
      return this.showMore.includes(index);
    },
    isTextBig(index) {
      return this.hasTextBig.includes(index);
    },
    updateAreaRelated() {
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.inner) {
            this.$refs.inner.forEach((inner, index) => {
              const { height: innerHeight } = inner.getBoundingClientRect();
              const { height: parentHeight } =
                inner.parentElement.getBoundingClientRect();

              if (innerHeight > parentHeight) {
                this.hasTextBig.push(index);
              } else {
                this.showMore = this.showMore.filter((item) => item !== index);
                this.hasTextBig = this.hasTextBig.filter(
                  (item) => item !== index,
                );
              }
            });
          }
          // Set short time out so it adds a delay to dimensions getting
        }, 500);
      });
    },
    slideToActivatedItem() {
      if (
        this.activeInstance === this.instanceId &&
        this.currentEditableItem != null
      ) {
        if (this.currentEditableItem.includes('slides')) {
          const itemPathIndex = Number(
            this.currentEditableItem.split('.').pop(),
          );
          if (this.swiper && itemPathIndex < this.swiper.slides.length) {
            this.swiper.slideToLoop(itemPathIndex);
          }
        }
      }
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'text',
            fields: [
              {
                name: 'mainTitle',
                type: 'ShortText',
                label: 'Título',
              },
              {
                name: 'titleWeight',
                after: 'mainTitle',
                isEnabled({ mainTitle }) {
                  return mainTitle;
                },
              },
              {
                name: 'mainSubtitle',
                type: 'ShortText',
                label: 'Subtítulo',
              },
              {
                name: 'mainText',
                type: 'Html',
                label: 'Texto de apoio',
                wrapWithParagraph: false,
              },
            ],
            toRemove: ['title', 'subtitle', 'text'],
          },
          {
            name: 'slides',
            label: 'Slides',
            fields: [
              {
                name: 'slides',
                type: 'List',
                itemLabel: 'Slider {index}', // any {index} will be replaced by item's index (1-based)
                addItemLabel: 'Adicionar slide',
                fields: [
                  ...extendMetadata({
                    extends: [BaseModule.metadata, TextAndMedia.metadata],
                    toRemove: ['config', 'gallery', 'buttons'],
                  }).tabs[0].groups[0].fields, // we only want the content group fields
                  {
                    name: 'image',
                    type: 'ImageFile',
                    maxWidth: 960,
                    label: 'Imagem',
                    helpText: 'Dimensões recomendadas: 490x480',
                  },
                  {
                    name: 'altText',
                    type: 'ShortText',
                    label: 'Texto alternativo',
                  },
                  {
                    name: 'differentMobileBackgroundImage',
                    type: 'Checkbox',
                    label: 'Usar uma imagem diferente para mobile',
                  },
                  {
                    name: 'imageMobile',
                    type: 'ImageFile',
                    maxWidth: 960,
                    label: 'Imagem mobile',
                    helpText: 'Dimensões recomendadas: 490x480',
                    isEnabled() {
                      return this.differentMobileBackgroundImage;
                    },
                  },
                  {
                    name: 'altTextMobile',
                    type: 'ShortText',
                    label: 'Texto alternativo mobile',
                    isEnabled() {
                      return this.differentMobileBackgroundImage;
                    },
                  },
                  {
                    name: 'showButton',
                    type: 'Checkbox',
                    label: 'Incluir botão',
                    default: false,
                  },
                  {
                    name: 'buttonText',
                    type: 'ShortText',
                    label: 'Texto do botão',
                    maxlength: 15,
                    isEnabled() {
                      return this.showButton;
                    },
                  },
                  {
                    name: 'buttonUrl',
                    type: 'Url',
                    isEnabled() {
                      return this.showButton;
                    },
                  },
                  {
                    name: 'buttonFeatured',
                    type: 'Checkbox',
                    label: 'Destacar botão',
                    isEnabled() {
                      return this.showButton;
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  i18n: {
    messages: {
      pt: {
        read: 'Ler',
        more: 'mais',
        less: 'menos',
      },
      en: {
        read: 'Read',
        more: 'more',
        less: 'less',
      },
      es: {
        read: 'Lea',
        more: 'más',
        less: 'menos',
      },
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/styles/libs/swiper';

.content-slider {
  padding: 0;
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);
  margin: 0;
  max-width: 100% !important;

  @include media-breakpoint-down(md) {
    padding-top: var(--padding-top-mobile);
    padding-bottom: var(--padding-bottom-mobile);
  }

  .content {
    max-width: 1200px;

    .header {
      max-width: 980px;
      padding-left: 20px;
      padding-right: 20px;
      margin: 0 auto 32px;

      .txt {
        line-height: 1.7em;

        &::v-deep {
          strong {
            font-weight: $lb-bold !important;
          }

          a,
          a:visited {
            color: inherit;
            text-decoration: underline;
          }
        }
      }
    }
  }
}

.swiper {
  .swiper-slide {
    width: 80%;

    @include media-breakpoint-up(lg) {
      width: 100%;
    }
  }

  .swiper-button-prev,
  .swiper-button-next {
    width: 60px;
    position: absolute;
    top: 240px;

    &::after {
      background-color: rgba($lb-black, 0.333);
      color: $lb-white;
      border-radius: 50px;
      width: 50px;
      height: 50px;
      font-size: 30px;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    @include media-breakpoint-down(xl) {
      display: none;
    }
  }

  &::v-deep {
    .swiper-pagination-bullet-active {
      background: var(--title-color, $lb-dark-gray) !important;
    }

    .swiper-pagination-bullets {
      padding-top: 32px;
      position: initial;

      .swiper-pagination-bullet {
        margin: 0;
        margin-left: 15px;
        width: 11px;
        height: 11px;
        padding: 11px 11px;
        border-radius: 0;
      }
    }
  }
}

::v-deep {
  .text-media {
    padding: 0 !important;
    max-width: 980px;
    margin: auto;
    text-align: initial;
    background: $lb-white;

    .swiper-slide {
      height: 100%;
    }

    .texto {
      padding: 6.25%;
      @include media-breakpoint-up(lg) {
        padding: 4.75% 6.25%;
      }

      .tit-g {
        color: $lb-dark-gray;
        font-size: 30px;
        font-weight: $lb-light;

        @include media-breakpoint-down(sm) {
          font-size: 24px;
        }
      }

      .sub {
        font-size: 20px;
        padding-top: 16px;
        color: $lb-dark-gray;
        opacity: 0.7;

        @include media-breakpoint-down(sm) {
          font-size: 18px;
          padding-top: 8px;
        }
      }

      .txt {
        flex: 1;

        .inner-text {
          color: $lb-medium-gray;
          max-height: (1.5 * 5 * 15px);

          &--show-more {
            max-height: unset !important;
          }

          @include media-breakpoint-up(lg) {
            max-height: unset !important;
          }
        }

        &--no-buttons {
          .inner-text {
            max-height: (1.5 * 8 * 15px);
          }
        }
      }
    }

    .expand-text {
      position: relative;
      margin-right: auto;
      border: 0;
      background: none;
      font-weight: $lb-bold;
      outline: inherit;
      padding: 0 14px 0 0;
      cursor: pointer;
      color: var(--primary-color);

      &::after {
        content: '';
        border-top: 7px solid;
        border-left: 5px solid transparent !important;
        border-right: 5px solid transparent !important;
        position: absolute;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
        transition: transform 0.4s ease-in-out;
      }

      &--active::after {
        transform: translateY(-50%) rotate(180deg);
      }
    }
  }
}
</style>
